import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scissors, Store, Boxes, Truck, Award, ClipboardList, Receipt, Headphones, Target, HeartHandshake } from "lucide-react";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";
import { OZENTYA_SHOP_URL } from "@/lib/social";
import { useProProducts, ProductTile } from "@/components/home/shared";
import { OrderContact } from "@/components/OrderContact";

const AUDIENCE = [
  [Scissors, "Barbiers", "Huiles à barbe en quatre parfums, shampoing pour barbe et finitions pour vos prestations."],
  [Award, "Barbershops", "Une gamme courte, à utiliser au fauteuil et à revendre au comptoir."],
  [Store, "Salons de coiffure", "Soins au bac, démêlants, masques et lotions pour cheveux afro, texturés et tous types de cheveux."],
  [Boxes, "Revendeurs et distributeurs", "Commande par conditionnement, factures professionnelles et réassort en un clic."],
];

const WHY = [
  [ClipboardList, "Un catalogue structuré", "Chaque produit avec sa catégorie, son conditionnement et son prix unitaire HT."],
  [Receipt, "Des informations transparentes", "TVA calculée automatiquement, devis et factures PDF dans votre compte."],
  [Truck, "Des conditions de livraison claires", "Offerte en France métropolitaine ; DOM-TOM de 30 à 60 € ; Belgique et Europe 50 € HT."],
  [Headphones, "Un contact direct", "Une question sur un produit ou une commande : notre équipe répond par e-mail ou WhatsApp."],
];

const Values = () => (
  <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 grid md:grid-cols-3 gap-8">
    {[
      [Target, "Notre approche", "Des produits que nous connaissons, des fiches claires, une commande en ligne simple et un accompagnement à l'écoute de vos questions."],
      [ArrowRight, "Notre ambition", "Accompagner les professionnels dans le développement de leur activité avec une offre adaptée à leurs prestations et à leur clientèle."],
      [HeartHandshake, "Notre engagement", "Une expérience d'achat claire, un catalogue structuré et des informations commerciales transparentes, sans promesse que nous ne pouvons pas tenir."],
    ].map(([Icon, t, d]) => (
      <div key={t} className="border-t-2 border-[var(--oz-gold)] pt-5">
        <Icon className="w-5 h-5 text-[var(--oz-gold)]" />
        <h2 className="mt-3 font-serif text-2xl">{t}</h2>
        <p className="mt-2 text-sm text-[var(--oz-muted)] leading-relaxed">{d}</p>
      </div>
    ))}
  </section>
);

const Offer = () => {
  const products = useProProducts();
  if (!products?.length) return null;
  const cats = [...new Set(products.map((p) => p.category))];
  const barbe = products.filter((p) => p.category === "Barbe").sort((a) => (a.slug === "shampoing-barbe" ? -1 : 0));
  return (
    <section data-testid="about-offer" className="bg-[var(--oz-bg-2)]">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl">Une offre pensée pour les professionnels</h2>
        <p className="mt-3 max-w-2xl text-[var(--oz-muted)]">{products.length} références Ozentya, réparties en {cats.length} catégories, vendues par conditionnement à prix HT.</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {cats.map((c) => <span key={c} className="rounded-full bg-white border border-[var(--oz-border)] px-4 py-2 text-sm">{c} · {products.filter((p) => p.category === c).length}</span>)}
        </div>
        <h3 className="mt-10 font-semibold text-lg">Soin et entretien de la barbe</h3>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">{barbe.map((p) => <ProductTile key={p.id} p={p} />)}</div>
      </div>
    </section>
  );
};

export default function AboutPage() {
  useSeo({
    title: "Qui sommes-nous ? Ozentya, partenaire des barbiers et salons de coiffure",
    description: "Ozentya, marque de soins capillaires et barbe pour barbiers, barbershops, salons de coiffure, revendeurs et distributeurs : catalogue pro à prix HT, commande en ligne, livraison France et Europe.",
    path: "/a-propos",
    image: "/img/home/about-hero.webp",
  });
  useJsonLd("ld-about", {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "AboutPage", name: "Qui sommes-nous ? — Ozentya", url: `${SITE_URL}/a-propos`, inLanguage: "fr-FR" },
      { "@type": "Organization", name: "Ozentya", url: SITE_URL, email: "pchaubo@hotmail.fr", sameAs: [OZENTYA_SHOP_URL],
        description: "Marque de soins capillaires et barbe pour les professionnels de la coiffure et du grooming." },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Qui sommes-nous ?", item: `${SITE_URL}/a-propos` }] },
    ],
  });
  return (
    <div data-testid="about-page">
      <section className="relative bg-[var(--oz-dark)] text-white overflow-hidden">
        <img src="/img/home/about-hero.webp" alt="Barbier et coiffeuse au travail dans un salon-barbershop" width="1264" height="848" fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover object-[65%_center] opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--oz-dark)] via-[var(--oz-dark)]/85 to-transparent" />
        <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-20 sm:py-28">
          <nav aria-label="Fil d'Ariane" className="text-xs text-white/60"><Link to="/" className="hover:text-white">Accueil</Link> / Qui sommes-nous ?</nav>
          <h1 data-testid="about-title" className="mt-5 max-w-2xl font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">OZENTYA, votre partenaire beauté et grooming pour les professionnels</h1>
        </div>
      </section>
      <section className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-14 sm:py-16 space-y-5 text-lg leading-relaxed">
        <p>Ozentya est une marque de soins capillaires et de soins de la barbe. Née de 15 ans d'expérience autour des cheveux afro, crépus et texturés et des problématiques de cuir chevelu, elle s'adresse aujourd'hui en priorité aux professionnels.</p>
        <p className="text-[var(--oz-muted)]">Notre mission : donner aux barbiers, aux salons de coiffure et aux revendeurs un accès direct à notre gamme, avec des prix professionnels clairs et une commande en ligne simple.</p>
      </section>
      <Offer />
      <section data-testid="about-audience" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl">À qui s'adresse OZENTYA ?</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {AUDIENCE.map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl bg-[var(--oz-dark)] text-white p-6">
              <Icon className="w-6 h-6 text-[var(--oz-gold)]" /><h3 className="mt-4 font-semibold text-lg">{t}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section data-testid="about-why" className="bg-[var(--oz-bg-2)]">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-20">
          <h2 className="font-serif text-3xl sm:text-4xl">Pourquoi choisir OZENTYA ?</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-7">
            {WHY.map(([Icon, t, d]) => (
              <div key={t} className="flex gap-4"><Icon className="w-6 h-6 shrink-0 text-[var(--oz-gold)]" /><div><h3 className="font-semibold">{t}</h3><p className="mt-1 text-sm text-[var(--oz-muted)] leading-relaxed">{d}</p></div></div>
            ))}
          </div>
        </div>
      </section>
      <Values />
      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-16 sm:pb-24 grid lg:grid-cols-2 gap-5">
        <div className="rounded-3xl bg-[var(--oz-dark)] text-white p-8 sm:p-10">
          <h2 className="font-serif text-3xl">Découvrez notre catalogue professionnel</h2>
          <p className="mt-3 text-white/70">Prix HT réservés aux comptes professionnels validés.</p>
          <Link to="/espace-pro" data-testid="about-cta-catalogue" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] px-7 py-3.5 text-sm font-bold hover:bg-[var(--oz-gold-hover)] transition-colors">Voir le catalogue <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="rounded-3xl border-2 border-[var(--oz-gold)] p-8 sm:p-10">
          <h2 className="font-serif text-3xl">Contactez notre équipe</h2>
          <p className="mt-3 text-[var(--oz-muted)]">Une question sur un produit, une commande ou l'ouverture d'un compte ?</p>
          <Link to="/contact" data-testid="about-cta-contact" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--oz-dark)] text-white px-7 py-3.5 text-sm font-bold hover:bg-black transition-colors">Nous contacter <ArrowRight className="w-4 h-4" /></Link>
          <OrderContact title="Ou directement :" className="mt-6" testid="about-order-contact" />
        </div>
      </section>
    </div>
  );
}
